import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { BehaviorSubject, map, Observable } from 'rxjs';
import * as moment from 'moment';
import { VariableBenefitAccountDetails } from '../../../../../models/transactions/variable-benefit-account-details';
import { RecurringPaymentInstruction } from '../../../../../models/submissions/recurring-payment-instruction';
import { RecurringPaymentCardData } from './recurring-payment-card-data';
import { payingInstructionOrNull } from '../../create-vb-payment-change-components/utils/vb-payment-change-payments-step.utils';

@Component({
  selector: 'app-vb-payments-recurring-payment-details-card',
  templateUrl: './vb-payments-recurring-payment-details-card.component.html',
  styleUrls: ['./vb-payments-recurring-payment-details-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VbPaymentsRecurringPaymentDetailsCardComponent {
  @Input({ required: true }) set accountDetails(value: VariableBenefitAccountDetails) {
    this._accountDetails.next(value);
  }

  private _accountDetails = new BehaviorSubject<VariableBenefitAccountDetails | null>(null);
  public accountDetails$ = this._accountDetails.asObservable();

  public monthlyPaymentData$: Observable<RecurringPaymentCardData | null> = this.accountDetails$.pipe(
    map(details => {
      if (!details) return null;
      return this.buildCardData(
        details.activeMonthlyRecurringPaymentInstruction,
        details.scheduledMonthlyRecurringPaymentInstruction,
        false
      );
    })
  );

  public annualPaymentData$: Observable<RecurringPaymentCardData | null> = this.accountDetails$.pipe(
    map(details => {
      if (!details) return null;
      return this.buildCardData(
        details.activeAnnualRecurringPaymentInstruction,
        details.scheduledAnnualRecurringPaymentInstruction,
        true
      );
    })
  );

  private buildCardData(
    currentInstruction: RecurringPaymentInstruction | null,
    scheduled: RecurringPaymentInstruction | null,
    isAnnual: boolean
  ): RecurringPaymentCardData {
    const active = payingInstructionOrNull(currentInstruction);

    if (!active && !scheduled) {
      return {
        rows: [
          {
            cardText: isAnnual ? $localize`No annual payments` : $localize`No monthly payments`,
            useMinimumAmount: null,
            paymentAmount: 0
          }
        ]
      };
    }

    if (!active && scheduled) {
      const scheduledDate = this.formatMonthYear(scheduled.paymentStartDate);
      return {
        rows: [
          {
            cardText: $localize`Payment starts ${scheduledDate}`,
            useMinimumAmount: scheduled.useMinimumAmount,
            paymentAmount: scheduled.paymentAmount
          }
        ]
      };
    }

    if (active && !scheduled) {
      return {
        rows: [
          {
            cardText: isAnnual ? $localize`${this.formatMonth(active.paymentStartDate)} payments` : null,
            useMinimumAmount: active.useMinimumAmount,
            paymentAmount: active.paymentAmount
          }
        ]
      };
    }

    // Active + scheduled
    if (active && scheduled) {
      const lastPaymentDate = moment
        .utc(active.endDate ?? scheduled.paymentStartDate)
        .subtract(1, isAnnual ? 'year' : 'month')
        .format('MMMM YYYY');
      const startingDate = this.formatMonthYear(scheduled.paymentStartDate);
      return {
        rows: [
          {
            cardText: $localize`Last payment in ${lastPaymentDate}`,
            useMinimumAmount: active.useMinimumAmount,
            paymentAmount: active.paymentAmount
          },
          {
            cardText: $localize`Starting ${startingDate}`,
            useMinimumAmount: scheduled.useMinimumAmount,
            paymentAmount: scheduled.paymentAmount
          }
        ]
      };
    }

    return { rows: [] };
  }

  private formatMonthYear(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
  }

  private formatMonth(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'long', timeZone: 'UTC' });
  }
}
